import React from 'react';
import { X, CheckCircle, AlertCircle, Loader2, Sparkles } from 'lucide-react';

export interface UploadTask {
  id: string;
  filename: string;
  status: 'uploading' | 'analyzing' | 'completed' | 'error';
  progress: number;
  error?: string;
  aiProgress?: number;
  visionResult?: {
    aiTags: string[];
    labelsCount: number;
    objectsCount: number;
    hasText: boolean;
  };
}

interface UploadProgressProps {
  tasks: UploadTask[];
  onClose: () => void;
}

const UploadProgress: React.FC<UploadProgressProps> = ({ tasks, onClose }) => {
  if (tasks.length === 0) return null;

  const completedCount = tasks.filter((t) => t.status === 'completed').length;
  const errorCount = tasks.filter((t) => t.status === 'error').length;
  const inProgressCount = tasks.filter((t) => 
    t.status === 'uploading' || t.status === 'analyzing'
  ).length;

  return (
    <div className="fixed top-4 right-4 z-50 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <div className="relative">
            {inProgressCount > 0 && (
              <Loader2 size={20} className="animate-spin" />
            )}
            {inProgressCount === 0 && errorCount === 0 && (
              <CheckCircle size={20} />
            )}
            {inProgressCount === 0 && errorCount > 0 && (
              <AlertCircle size={20} />
            )}
          </div>
          <div>
            <h3 className="font-bold text-sm">上傳進度</h3>
            <p className="text-xs text-blue-100">
              {inProgressCount > 0 ? `處理中 ${inProgressCount}` : 
               errorCount > 0 ? `完成 ${completedCount} / 失敗 ${errorCount}` :
               `已完成 ${completedCount}`}
            </p>
          </div>
        </div>
        {inProgressCount === 0 && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            <X size={18} className="text-white" />
          </button>
        )}
      </div>

      {/* Task List */}
      <div className="max-h-80 overflow-y-auto">
        {tasks.map((task) => (
          <div
            key={task.id}
            className="px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-start gap-3">
              {/* Status Icon */}
              <div className="shrink-0 mt-0.5">
                {task.status === 'completed' && (
                  <CheckCircle size={18} className="text-green-500" />
                )}
                {task.status === 'error' && (
                  <AlertCircle size={18} className="text-red-500" />
                )}
                {(task.status === 'uploading' || task.status === 'analyzing') && (
                  <Loader2 size={18} className="text-blue-500 animate-spin" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate" title={task.filename}>
                  {task.filename}
                </p>

                {/* Status Text */}
                {task.status === 'uploading' && (
                  <p className="text-xs text-slate-500 mt-0.5">正在上傳...</p>
                )}
                {task.status === 'analyzing' && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Sparkles size={12} className="text-blue-500" />
                    <p className="text-xs text-blue-600 font-medium">Google Vision 辨識中...</p>
                  </div>
                )}
                {task.status === 'completed' && (
                  <div className="mt-0.5">
                    <p className="text-xs text-green-600 font-medium">✓ 完成</p>
                    {task.visionResult && task.visionResult.aiTags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {task.visionResult.aiTags.slice(0, 5).map((tag, idx) => (
                          <span
                            key={idx}
                            className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded border border-blue-200"
                          >
                            {tag}
                          </span>
                        ))}
                        {task.visionResult.aiTags.length > 5 && (
                          <span className="text-[10px] text-slate-500">
                            +{task.visionResult.aiTags.length - 5}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {task.status === 'error' && (
                  <p className="text-xs text-red-600 mt-0.5">{task.error}</p>
                )}

                {/* Progress Bar */}
                {(task.status === 'uploading' || task.status === 'analyzing') && (
                  <div className="mt-2">
                    <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          task.status === 'analyzing'
                            ? 'bg-gradient-to-r from-blue-500 to-purple-500'
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UploadProgress;
